/**
 * Mutations for leaving / kicking World Cup (competition) squad members.
 *
 * Uses mutateAsync + awaited query invalidation so the leaderboard UI updates
 * before the success snackbar (snackbar + removal feel simultaneous).
 */

import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useApiClient, isRateLimitError } from '../../../helpers/apiClient.helper';
import { useDialog } from '../../shared/hooks/useDialog';
import { useSnackbar } from '../../shared/hooks/useSnackbar';
import {
  leaveWorldCupCompetitionGroup,
  removeWorldCupCompetitionGroupMember,
  type RemoveWorldCupGroupMemberResult,
} from '../helpers/worldCupCompetitionGroupsApi';
import { worldCupQueryKeys } from '../constants/worldcupQueryKeys';
import { competitionGroupsRoute } from '../../competitions/helpers/competitionRoutes.helper';
import { useCompetitionId, useCompetitionBasePath } from '../../competitions/contexts/CompetitionContext';
import {
  WORLD_CUP_MEMBERSHIP_COPY,
  buildKickMemberMessage,
  buildLeaveSquadMessage,
  buildSquadNameLabel,
} from './useWorldCupGroupMembershipMutations.constants';

interface UseWorldCupGroupMembershipMutationsArgs {
  groupId: string;
  /** The caller's membership id (from `useWorldCupGroupDetailData.currentUserMembershipId`). */
  currentUserMembershipId: string | null;
}

interface LeaveSquadOptions {
  squadName?: string;
}

interface KickMemberOptions {
  membershipId: string;
  memberName: string;
}

export function useWorldCupGroupMembershipMutations({
  groupId,
  currentUserMembershipId,
}: UseWorldCupGroupMembershipMutationsArgs) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const router = useRouter();
  const competitionId = useCompetitionId();
  const basePath = useCompetitionBasePath();
  const { showDialog, showAlert } = useDialog();
  const { showSnackbar } = useSnackbar();

  const [kickUiBusy, setKickUiBusy] = useState(false);
  const [leaveUiBusy, setLeaveUiBusy] = useState(false);
  const kickFlowLockRef = useRef(false);
  const leaveFlowLockRef = useRef(false);

  const invalidateSquadQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: worldCupQueryKeys.groups(competitionId),
      }),
      queryClient.invalidateQueries({
        queryKey: worldCupQueryKeys.groupLeaderboardAll(competitionId, groupId),
      }),
    ]);
  }, [queryClient, groupId]);

  const leaveMutation = useMutation({
    mutationFn: async () => {
      if (!currentUserMembershipId) {
        throw new Error(WORLD_CUP_MEMBERSHIP_COPY.NOT_MEMBER_ERROR);
      }
      return leaveWorldCupCompetitionGroup(apiClient, competitionId, groupId, currentUserMembershipId);
    },
  });

  const kickMutation = useMutation({
    mutationFn: async ({ membershipId }: { membershipId: string }) => {
      return removeWorldCupCompetitionGroupMember(apiClient, competitionId, groupId, membershipId);
    },
  });

  const confirmAndLeaveSquad = useCallback(
    async (options?: LeaveSquadOptions) => {
      const squadLabel = buildSquadNameLabel(options?.squadName);
      const confirmed = await showDialog({
        title: WORLD_CUP_MEMBERSHIP_COPY.LEAVE_DIALOG_TITLE,
        message: buildLeaveSquadMessage(squadLabel),
        type: 'danger',
        confirmText: WORLD_CUP_MEMBERSHIP_COPY.LEAVE_DIALOG_CONFIRM,
        cancelText: WORLD_CUP_MEMBERSHIP_COPY.LEAVE_DIALOG_CANCEL,
      });
      if (!confirmed) return;

      if (leaveFlowLockRef.current) return;
      leaveFlowLockRef.current = true;
      setLeaveUiBusy(true);
      try {
        const result: RemoveWorldCupGroupMemberResult = await leaveMutation.mutateAsync();
        await invalidateSquadQueries();
        showSnackbar(
          result.groupDeleted
            ? WORLD_CUP_MEMBERSHIP_COPY.SQUAD_DELETED_SNACKBAR
            : WORLD_CUP_MEMBERSHIP_COPY.LEFT_SQUAD_SNACKBAR,
          'success',
        );
        router.replace(competitionGroupsRoute(basePath) as never);
      } catch (error: unknown) {
        if (!isRateLimitError(error)) {
          const message =
            error instanceof Error ? error.message : WORLD_CUP_MEMBERSHIP_COPY.LEAVE_ERROR_FALLBACK;
          await showAlert({
            title: WORLD_CUP_MEMBERSHIP_COPY.ERROR_TITLE,
            message,
            type: 'danger',
          });
        }
      } finally {
        setLeaveUiBusy(false);
        leaveFlowLockRef.current = false;
      }
    },
    [leaveMutation, showDialog, showAlert, invalidateSquadQueries, showSnackbar, router],
  );

  const confirmAndKickMember = useCallback(
    async ({ membershipId, memberName }: KickMemberOptions) => {
      const confirmed = await showDialog({
        title: WORLD_CUP_MEMBERSHIP_COPY.KICK_DIALOG_TITLE,
        message: buildKickMemberMessage(memberName),
        type: 'danger',
        confirmText: WORLD_CUP_MEMBERSHIP_COPY.KICK_DIALOG_CONFIRM,
        cancelText: WORLD_CUP_MEMBERSHIP_COPY.KICK_DIALOG_CANCEL,
      });
      if (!confirmed) return;

      if (kickFlowLockRef.current) return;
      kickFlowLockRef.current = true;
      setKickUiBusy(true);
      try {
        const result: RemoveWorldCupGroupMemberResult = await kickMutation.mutateAsync({
          membershipId,
        });
        await invalidateSquadQueries();
        showSnackbar(
          result.groupDeleted
            ? WORLD_CUP_MEMBERSHIP_COPY.SQUAD_DELETED_SNACKBAR
            : WORLD_CUP_MEMBERSHIP_COPY.MEMBER_REMOVED_SNACKBAR,
          'success',
        );
        if (result.groupDeleted) {
          router.replace(competitionGroupsRoute(basePath) as never);
        }
      } catch (error: unknown) {
        if (!isRateLimitError(error)) {
          const message =
            error instanceof Error ? error.message : WORLD_CUP_MEMBERSHIP_COPY.KICK_ERROR_FALLBACK;
          await showAlert({
            title: WORLD_CUP_MEMBERSHIP_COPY.ERROR_TITLE,
            message,
            type: 'danger',
          });
        }
      } finally {
        setKickUiBusy(false);
        kickFlowLockRef.current = false;
      }
    },
    [kickMutation, showDialog, showAlert, invalidateSquadQueries, showSnackbar, router],
  );

  return {
    confirmAndLeaveSquad,
    confirmAndKickMember,
    isLeaving: leaveMutation.isPending || leaveUiBusy,
    isKicking: kickMutation.isPending || kickUiBusy,
  };
}
